import React from 'react';
import { Map, CheckCircle2, Clock, AlertCircle, Sparkles, ShoppingBag, Heart, DollarSign, Users, Trophy, Handshake, Bell, Moon, MessageSquare } from 'lucide-react';

const AdminRoadmapPage = () => {
  const completed = [
    { title: 'News Aggregator/Crawler', description: 'RSS feeds, article review workflow, auto-crawl at 6pm Sydney' },
    { title: 'Password Reset via Email', description: 'Resend integration, forgot/reset password pages' },
    { title: 'Email Notifications', description: 'Welcome emails, news alerts, blog notifications' },
    { title: 'BIMI Email Logo Setup', description: 'DNS records configured for email logo display' },
    { title: 'Shop Coming Soon Page', description: 'Placeholder at /shop with notify signup' },
    { title: 'Scheduled Articles Tab', description: 'View and manage scheduled news articles' },
    { title: 'Admin Role Management', description: 'Promote/demote users to admin' },
  ];

  const upcoming = [
    { title: 'SMS Support (Twilio)', description: 'Account recovery via SMS', priority: 'P1', icon: MessageSquare },
    { title: 'Day/Night Mode', description: 'System-aware light/dark toggle', priority: 'P2', icon: Moon },
  ];

  const knownIssues = [
    { title: 'Log Symptoms Modal Mobile UI', description: 'Needs design input', priority: 'P1' },
    { title: 'Supplements API Error', description: 'POST /api/supplements returns 500', priority: 'P2' },
    { title: 'Admin Read-Only Security', description: 'Mutation blocking incomplete', priority: 'P3' },
  ];

  const shopPlans = [
    { title: 'Shopify Integration', description: 'Connect to mycbc.shop for products and memberships' },
    { title: 'Member Discounts', description: 'Public pays RRP, members get discounts' },
    { title: '5% to Research', description: '5% of every purchase supports CLL research' },
  ];

  const fundraising = {
    direct: [
      { title: 'Donate Button', description: 'Link to CLL Society or Leukemia Foundation' },
      { title: 'Round-up Donations', description: 'Round up shop purchases to donate the difference' },
      { title: 'Milestone Fundraisers', description: 'Members create pages for their CLL-versary' },
    ],
    community: [
      { title: 'Matched Donations', description: 'MyCBC matches a % of member donations' },
      { title: 'Shop for a Cause', description: '% of shop profits go to research' },
      { title: 'Member Challenges', description: 'Wellness goals where sponsors donate per milestone' },
    ],
    awareness: [
      { title: 'Research News Section', description: 'Highlight studies that need funding' },
      { title: 'Fund This Study', description: 'Partner with researchers to spotlight trials' },
      { title: 'Survivor Stories', description: 'Share how research made a difference' },
    ],
    partnerships: [
      { title: 'CLL Society Partnership', description: 'Official fundraising partner' },
      { title: 'Corporate Sponsors', description: 'Companies sponsor the platform' },
      { title: 'Pharmaceutical Partnerships', description: 'Ethical awareness campaigns' },
    ],
    gamification: [
      { title: 'Donation Badges', description: 'Members earn badges for contributing' },
      { title: 'Leaderboards', description: 'Community donation milestones (anonymized)' },
      { title: 'Matching Months', description: 'Special campaigns where donations are matched' },
    ],
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-3">
        <Map className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Roadmap</h1>
          <p className="text-slate-600">MyCBC development plans and progress</p>
        </div>
      </div>

      {/* Recently Completed */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <CheckCircle2 className="h-5 w-5 text-green-500" />
          <h2 className="text-xl font-semibold text-slate-900">Recently Completed</h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {completed.map((item, i) => (
            <div key={i} className="flex items-start gap-3 p-3 bg-green-50 rounded-xl">
              <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-slate-900">{item.title}</p>
                <p className="text-sm text-slate-600">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Upcoming */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="h-5 w-5 text-blue-500" />
          <h2 className="text-xl font-semibold text-slate-900">Upcoming</h2>
        </div>
        <div className="space-y-3">
          {upcoming.map((item, i) => {
            const Icon = item.icon;
            return (
              <div key={i} className="flex items-start gap-3 p-3 bg-blue-50 rounded-xl">
                <Icon className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-slate-900">{item.title}</p>
                    <span className="px-2 py-0.5 text-xs font-medium bg-blue-200 text-blue-800 rounded-full">{item.priority}</span>
                  </div>
                  <p className="text-sm text-slate-600">{item.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Known Issues */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <AlertCircle className="h-5 w-5 text-amber-500" />
          <h2 className="text-xl font-semibold text-slate-900">Known Issues</h2>
        </div>
        <div className="space-y-3">
          {knownIssues.map((item, i) => (
            <div key={i} className="flex items-start gap-3 p-3 bg-amber-50 rounded-xl">
              <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-slate-900">{item.title}</p>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                    item.priority === 'P1' ? 'bg-red-200 text-red-800' :
                    item.priority === 'P2' ? 'bg-amber-200 text-amber-800' :
                    'bg-slate-200 text-slate-800'
                  }`}>{item.priority}</span>
                </div>
                <p className="text-sm text-slate-600">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Shop Plans */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <ShoppingBag className="h-5 w-5 text-purple-500" />
          <h2 className="text-xl font-semibold text-slate-900">Shop Integration</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          {shopPlans.map((item, i) => (
            <div key={i} className="p-4 bg-purple-50 rounded-xl">
              <p className="font-medium text-slate-900">{item.title}</p>
              <p className="text-sm text-slate-600">{item.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Fundraising for Research */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-6">
          <Heart className="h-5 w-5 text-rose-500" />
          <h2 className="text-xl font-semibold text-slate-900">Fundraising for CLL Research</h2>
        </div>

        <div className="space-y-6">
          {/* Direct Fundraising */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <DollarSign className="h-4 w-4 text-green-600" />
              <h3 className="font-semibold text-slate-800">Direct Fundraising</h3>
            </div>
            <div className="grid sm:grid-cols-3 gap-2">
              {fundraising.direct.map((item, i) => (
                <div key={i} className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium text-sm text-slate-900">{item.title}</p>
                  <p className="text-xs text-slate-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Community-Driven */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Users className="h-4 w-4 text-blue-600" />
              <h3 className="font-semibold text-slate-800">Community-Driven</h3>
            </div>
            <div className="grid sm:grid-cols-3 gap-2">
              {fundraising.community.map((item, i) => (
                <div key={i} className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium text-sm text-slate-900">{item.title}</p>
                  <p className="text-xs text-slate-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Awareness & Engagement */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Bell className="h-4 w-4 text-amber-600" />
              <h3 className="font-semibold text-slate-800">Awareness & Engagement</h3>
            </div>
            <div className="grid sm:grid-cols-3 gap-2">
              {fundraising.awareness.map((item, i) => (
                <div key={i} className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium text-sm text-slate-900">{item.title}</p>
                  <p className="text-xs text-slate-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Partnerships */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Handshake className="h-4 w-4 text-purple-600" />
              <h3 className="font-semibold text-slate-800">Partnerships</h3>
            </div>
            <div className="grid sm:grid-cols-3 gap-2">
              {fundraising.partnerships.map((item, i) => (
                <div key={i} className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium text-sm text-slate-900">{item.title}</p>
                  <p className="text-xs text-slate-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Gamification */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Trophy className="h-4 w-4 text-teal-600" />
              <h3 className="font-semibold text-slate-800">Gamification</h3>
            </div>
            <div className="grid sm:grid-cols-3 gap-2">
              {fundraising.gamification.map((item, i) => (
                <div key={i} className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium text-sm text-slate-900">{item.title}</p>
                  <p className="text-xs text-slate-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminRoadmapPage;
